import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";

const WHATSAPP_NUMBER = "971501889395";
const ENQUIRY_EMAIL = "atstextiletradingllc@gmail.com";

const CONTACT = {
  address: "C1801-89, Ontario Tower, A'Amal St, Business Bay, Dubai, UAE",
  phone: "(+971) 50 188 9395",
  email: "atstextiletradingllc@gmail.com",
  hours: "Mon – Sat · 9:00 – 18:00 GST",
};

const CATEGORIES = [
  "Women's Fabric Wholesale",
  "Women's Wholesale",
  "Men's Wholesale",
  "Arabic Wear Wholesale",
  "Sportswear Wholesale",
  "Accessories Wholesale",
  "Perfume Wholesale",
  "Footwear Wholesale",
  "Private Label / Custom",
];

const empty = { company_name: "", contact_person: "", email: "", category: CATEGORIES[0], quantity: "", destination: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`https://formsubmit.co/ajax/${ENQUIRY_EMAIL}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: `New Wholesale Enquiry — ${form.company_name}`,
          _template: "table",
          _captcha: "false",
          Company: form.company_name,
          "Contact Person": form.contact_person || "-",
          Email: form.email || "-",
          Category: form.category,
          Quantity: form.quantity || "-",
          Destination: form.destination || "-",
          Message: form.message,
        }),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success("Enquiry emailed — our wholesale desk will reply within one business day.");
      setForm(empty);
    } catch {
      toast.error(`Could not send. Please email us directly at ${ENQUIRY_EMAIL}`);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[#0A0B0E] border border-[#D4AF37]/20 focus:border-[#D4AF37]/70 outline-none px-4 py-3.5 text-sm font-light text-[#F8F5EE] placeholder:text-slate-500 transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="py-20 lg:py-32 px-4 sm:px-8 lg:px-16 bg-[#12141B]">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#D4AF37]/80 mb-4">Contact Us — B2B Enquiry / RFQ</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-snug max-w-3xl">
            Let's put together the right <span className="italic text-gold-gradient">wholesale order</span> for your business.
          </h2>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 space-y-px bg-[#D4AF37]/15 border border-[#D4AF37]/15 self-start"
            data-testid="contact-details-card"
          >
            {[
              { icon: MapPin, label: "Head Office", value: CONTACT.address, testid: "contact-address" },
              { icon: Phone, label: "Phone / WhatsApp", value: CONTACT.phone, testid: "contact-phone", href: `tel:${CONTACT.phone.replace(/[^+\d]/g, "")}` },
              { icon: Mail, label: "Email", value: CONTACT.email, testid: "contact-email", href: `mailto:${CONTACT.email}` },
              { icon: Clock, label: "Working Hours", value: CONTACT.hours, testid: "contact-hours" },
            ].map((row) => (
              <div key={row.label} className="bg-[#181B24] p-7 flex gap-5">
                <row.icon size={20} strokeWidth={1.5} className="text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <p className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#D4AF37]/70">{row.label}</p>
                  {row.href ? (
                    <a href={row.href} data-testid={row.testid} className="block mt-2 text-base sm:text-lg font-light text-[#F8F5EE] hover:text-[#D4AF37] transition-colors">
                      {row.value}
                    </a>
                  ) : (
                    <p data-testid={row.testid} className="mt-2 text-base sm:text-lg font-light text-[#F8F5EE]">
                      {row.value}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-7 bg-[#181B24] border border-[#D4AF37]/15 p-8 lg:p-10"
            data-testid="rfq-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <input data-testid="rfq-company-input" required placeholder="Company Name *" value={form.company_name} onChange={set("company_name")} className={inputCls} />
              <input data-testid="rfq-contact-person-input" placeholder="Contact Person" value={form.contact_person} onChange={set("contact_person")} className={inputCls} />
              <input data-testid="rfq-email-input" type="email" placeholder="Business Email" value={form.email} onChange={set("email")} className={inputCls} />
              <select data-testid="rfq-category-select" value={form.category} onChange={set("category")} className={inputCls}>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c} className="bg-[#0A0B0E]">
                    {c}
                  </option>
                ))}
              </select>
              <input data-testid="rfq-quantity-input" placeholder="Estimated Quantity (e.g. 500 pcs)" value={form.quantity} onChange={set("quantity")} className={inputCls} />
              <input data-testid="rfq-destination-input" placeholder="Destination (e.g. Riyadh, KSA)" value={form.destination} onChange={set("destination")} className={inputCls} />
            </div>
            <textarea
              data-testid="rfq-message-input"
              required
              rows={5}
              placeholder="Tell us about your requirement — styles, fabric, target price… *"
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} mt-5 resize-none`}
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="rfq-submit-button"
              className="mt-7 w-full sm:w-auto px-10 py-4 bg-[#D4AF37] text-[#0A0B0E] text-xs font-semibold tracking-[0.22em] uppercase hover:bg-[#E5C158] disabled:opacity-50 transition-colors duration-300 inline-flex items-center justify-center gap-2"
            >
              <Send size={15} />
              {sending ? "Sending…" : "Send Email Enquiry"}
            </button>
            <p className="mt-4 text-xs font-light text-slate-500">
              Prefer WhatsApp?{" "}
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rfq-whatsapp-fallback"
                className="text-[#D4AF37] hover:text-[#E5C158] transition-colors"
              >
                Chat with us on (+971) 50 188 9395
              </a>
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
